// Müşteri Portalları — operatör listesi (C2 Faz D read-model'inin arayüzü).
//
// Backend hazırdı: notify/lib/customer-portals.ts + routes-v2.ts `GET /customer-portals`
// (`/api/v2` altında, isOperatorSession korumalı). Ama HİÇBİR frontend tüketicisi yoktu →
// staff hangi müşterinin portalı olduğunu, kimin giriş yaptığını, verisinin ne kadar taze
// olduğunu göremiyordu; bir portalı açmak için slug'ı ezbere bilip URL'e yazmak gerekiyordu.
// Bkz. [[audits/2026-07-29-client-os-durum-tespiti]] K6 + K7.
//
// Bu görünüm salt-okunur; hiçbir mutasyon yapmaz. "Aç" bağlantısı staff önizlemesini
// `?tenant=<slug>` ile açar (main.ts bu parametreyi görünce doğrudan Client OS'a gider).

import { apiFetch } from '../api.js';
import { escHtml, escAttr } from '../utils.js';

interface PortalRow {
  slug: string;
  display_name: string;
  sector: string | null;
  parent_group: string | null;
  subscriptions: string[];
  lifecycle_state: string;
  owner_agent: string | null;
  user_count: number;
  active_users: number;
  has_login: boolean;
  gm_agent_id: string | null;
  castacc_mapped: boolean;
  open_tickets: number;
  ads_last_day: string | null;
}
interface PortalsResponse {
  ok: boolean;
  portals: PortalRow[];
  total: number;
  withLogin: number;
  openTicketsTotal: number;
}

type VS = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; data: PortalsResponse };
let vs: VS = { kind: 'loading' };

export async function loadMusteriPortallari(): Promise<void> {
  vs = { kind: 'loading' };
  try {
    const r = await apiFetch('/api/v2/customer-portals');
    const j = await r.json().catch(() => ({ ok: false, error: 'geçersiz yanıt' }));
    if (!r.ok || !j.ok) {
      // 403 = operatör oturumu değil (tenant oturumuyla açılmış olabilir).
      vs = { kind: 'error', message: r.status === 403 ? 'Bu liste yalnız operatör oturumunda görünür.' : (j.error || ('HTTP ' + r.status)) };
      return;
    }
    vs = { kind: 'ready', data: j as PortalsResponse };
  } catch (e) { vs = { kind: 'error', message: (e as Error).message }; }
}

// Reklam verisi tazeliği — client-os.ts'teki eşikle aynı (scheduled-reports.ts STALE_DAYS).
const STALE_DAYS = 7;
function adsFreshness(lastDay: string | null): string {
  if (!lastDay) return '<span class="gb-pill gb-pill-muted">veri yok</span>';
  const end = Date.parse(lastDay + 'T00:00:00Z');
  if (!Number.isFinite(end)) return '<span class="gb-pill gb-pill-muted">—</span>';
  const days = Math.floor((Date.now() - end) / 86400000);
  const tone = days > STALE_DAYS ? 'ruby' : 'emerald';
  const txt = days <= 0 ? 'bugün' : `${days} gün önce`;
  return `<span class="gb-pill gb-pill-${tone}" title="Son veri günü: ${escAttr(lastDay)}">${escHtml(txt)}</span>`;
}

function chip(label: string, value: number | string, color: string): string {
  return `<div class="rt-card" style="border-left:4px solid ${color};padding:14px;">
    <div style="font-size:11px;color:var(--muted);text-transform:uppercase;">${escHtml(label)}</div>
    <div style="font-size:24px;font-weight:600;margin-top:6px;">${escHtml(String(value))}</div>
  </div>`;
}

function row(p: PortalRow): string {
  const name = `<div class="gb-c-name">${escHtml(p.display_name || p.slug)}</div>
    <div style="font-size:11px;color:var(--muted)">${escHtml(p.slug)}${p.parent_group ? ' · ' + escHtml(p.parent_group) : ''}</div>`;
  const login = p.has_login
    ? `<span class="gb-pill gb-pill-emerald">${p.active_users}/${p.user_count} aktif</span>`
    : '<span class="gb-pill gb-pill-amber">giriş yok</span>';
  const tickets = p.open_tickets > 0
    ? `<span class="gb-pill gb-pill-ruby">${p.open_tickets}</span>`
    : '<span class="gb-pill gb-pill-muted">0</span>';
  const castacc = p.castacc_mapped
    ? '<span class="gb-pill gb-pill-emerald">eşleşti</span>'
    : '<span class="gb-pill gb-pill-muted">yok</span>';
  return `<tr>
    <td>${name}</td>
    <td><span class="gb-pill gb-pill-sapphire">${escHtml(p.lifecycle_state || '—')}</span></td>
    <td>${escHtml(String(p.subscriptions.length))}</td>
    <td>${login}</td>
    <td>${tickets}</td>
    <td>${adsFreshness(p.ads_last_day)}</td>
    <td>${castacc}</td>
    <td>${escHtml(p.owner_agent || '—')}</td>
    <td><a class="gb-pill gb-pill-sapphire" href="?tenant=${encodeURIComponent(p.slug)}">Aç →</a></td>
  </tr>`;
}

export function renderMusteriPortallari(): void {
  const root = document.getElementById('view-musteri-portallari');
  if (!root) return;
  const header = `<div class="view-header">
      <div>
        <div class="view-title" id="musteri-portallari-title">🪟 Müşteri Portalları</div>
        <div class="view-sub">Hangi müşterinin portalı var, kim giriş yapmış, verisi ne kadar taze</div>
      </div>
    </div>`;
  if (vs.kind === 'loading') { root.innerHTML = header + '<div class="empty-state">Yükleniyor…</div>'; return; }
  if (vs.kind === 'error') {
    root.innerHTML = header + `<div class="empty-state"><div class="empty-icon">⚠️</div><div class="empty-title">${escHtml(vs.message)}</div></div>`;
    return;
  }
  const d = vs.data;
  const rows = d.portals.map(row).join('');
  root.innerHTML = header + `
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;">
      ${chip('Portal', d.total, '#60a5fa')}
      ${chip('Girişi olan', `${d.withLogin}/${d.total}`, '#34d399')}
      ${chip('Açık destek', d.openTicketsTotal, '#f87171')}
    </div>
    <div class="rt-card" style="margin-top:12px;">
      <div class="rt-card-title">Portallar (${d.portals.length})</div>
      ${d.portals.length === 0
        ? '<div class="empty-state"><div class="empty-icon">📂</div><div class="empty-title">Portal kaydı yok — customer_lifecycle boş.</div></div>'
        : `<table class="gb-table"><thead><tr>
            <th>Müşteri</th><th>Yaşam döngüsü</th><th>Abonelik</th><th>Giriş</th>
            <th>Açık destek</th><th>Reklam verisi</th><th>Castacc</th><th>Sahip ajan</th><th></th>
          </tr></thead><tbody>${rows}</tbody></table>`
      }
      <div class="gb-mod-note" style="margin-top:8px">Salt-okunur. "Aç" müşteri panelini staff önizlemesiyle açar.</div>
    </div>`;
}
